import { useGetDomainMetaData } from 'src/use-cases/invoice/useInvoices';
import { useEncodedQueryState } from 'src/hooks/useEncodedQueryState';
import { useEffect } from 'react';
import { CircularProgress } from '@mui/material';
import { AnalyticSearchRequestDto } from 'src/generated/services/TFinancialApi';
import { ChartViewType } from '../analytics/sidebar/types';
import { IPerformanceStateProps } from './types';
import { LandingTopWidget } from '../landing';
import { PerformanceAnalysisMain } from './Main';
import { LayoutSidebar } from '../sidebar/LayoutSidebar';

const defaultFiltersState: IPerformanceStateProps = {
  name: 'New Report',
  description: 'Report Description',
  filters: {
    selectedChartView: ChartViewType.TREND,
    formValues: {
      currencyReference: null,
      dateReference: 'PICKUP_DATE',
      granularity: 'MONTHLY',
      filters: {
        dateFilters: {},
        tradeLaneFilters: {},
        shipmentFilters: {},
      },
      invoiceStatusSet: ['APPROVED', 'REJECTED', 'FLAGGED'],
    } as AnalyticSearchRequestDto,
  },
};

export const PerformanceContainer = () => {
  const [hashedFilter, setHashedFilter] = useEncodedQueryState(defaultFiltersState, 'report');
  const { data: dataStatus, isLoading } = useGetDomainMetaData();
  const defaultCurrency = dataStatus?.defaultCurrency || null;

  // Ensure that hashedFilter has the expected structure
  useEffect(() => {
    // Check if the filters or formValues are missing or undefined
    const needsInitialization = !hashedFilter?.filters || !hashedFilter?.filters?.formValues || !hashedFilter?.filters?.selectedChartView;

    // Check if currencyReference is missing but we have a default currency
    const needsCurrency = !hashedFilter?.filters?.formValues?.currencyReference && defaultCurrency;

    if (needsInitialization) {
      // If the structure is broken, reset to default state
      setHashedFilter(defaultFiltersState);
    } else if (needsCurrency) {
      // Only update the currency reference
      setHashedFilter({
        ...hashedFilter,
        filters: {
          ...hashedFilter.filters,
          formValues: {
            ...hashedFilter.filters.formValues,
            currencyReference: defaultCurrency,
          },
        },
      });
    }
  }, [defaultCurrency, hashedFilter, setHashedFilter]);

  // Show layout with loading spinner inside content area while loading
  if (isLoading || !hashedFilter?.filters?.formValues?.currencyReference) {
    return (
      <div className='h-full overflow-auto bg-neutral-10'>
        <div className='relative bg-neutral-10'>
          <LandingTopWidget />
          <LayoutSidebar>
            <div className='flex-1 flex items-center justify-center min-h-[400px] bg-neutral-10'>
              <CircularProgress color='primary' />
            </div>
          </LayoutSidebar>
        </div>
      </div>
    );
  }

  return (
    <div className='h-full overflow-auto bg-neutral-10'>
      <div className='relative bg-neutral-10'>
        <LandingTopWidget />
        <LayoutSidebar>
          <PerformanceAnalysisMain hashedFilter={hashedFilter} setHashedFilter={setHashedFilter} />
        </LayoutSidebar>
      </div>
    </div>
  );
};
